import { useState, useEffect, useCallback } from 'react';
import { AdminPageShell } from '../components/AdminShared';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Shield, FileText, AlertTriangle, ClipboardList, Search, Plus, Edit, Trash2, CheckCircle, Clock, XCircle, Download, Loader2, Send, Mail, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers } })
    .then(async r => { if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || r.statusText); } return r.json(); });
}

const SOA_STATUSES = ['not_started', 'in_progress', 'implemented', 'not_applicable'];
const SOA_STATUS_LABELS = { not_started: 'Not Started', in_progress: 'In Progress', implemented: 'Implemented', not_applicable: 'N/A' };
const SOA_STATUS_COLORS = { not_started: 'bg-muted text-muted-foreground', in_progress: 'bg-amber-900/20 text-amber-400', implemented: 'bg-green-900/20 text-green-400', not_applicable: 'bg-muted text-muted-foreground' };
const DOC_STATUSES = ['draft', 'in_review', 'approved'];
const DOC_STATUS_COLORS = { draft: 'bg-muted text-muted-foreground', in_review: 'bg-amber-900/20 text-amber-400', approved: 'bg-green-900/20 text-green-400' };
const RISK_TREATMENTS = ['mitigate', 'accept', 'transfer', 'avoid'];

// Shared select styling that respects theme
const selectCls = "w-full mt-1 h-10 px-3 border border-border rounded-md text-sm bg-background text-foreground";
const selectSmCls = "w-full mt-1 h-9 px-3 border border-border rounded-md text-sm bg-background text-foreground";
const textareaCls = "w-full mt-1 px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground resize-none";

// ─── SCHEDULED REPORTS PANEL ───
function ScheduledReportsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => { api('/api/admin/compliance/report-settings').then(setSettings).catch(() => {}); }, []);

  const save = async (updates) => {
    setSaving(true);
    try {
      const res = await api('/api/admin/compliance/report-settings', { method: 'PUT', body: JSON.stringify(updates) });
      setSettings(res);
      toast.success('Report settings saved');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api('/api/admin/compliance/report-send', { method: 'POST' });
      toast.success('Compliance report sent to all recipients');
      api('/api/admin/compliance/report-settings').then(setSettings);
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const addRecipient = () => {
    if (!newEmail.trim() || !newEmail.includes('@')) return;
    const updated = [...(settings?.recipients || []), newEmail.trim()];
    save({ ...settings, recipients: updated });
    setNewEmail('');
  };

  const removeRecipient = (email) => {
    const updated = (settings?.recipients || []).filter(e => e !== email);
    save({ ...settings, recipients: updated });
  };

  const toggleEnabled = () => {
    save({ ...settings, enabled: !settings?.enabled });
  };

  if (!settings) return <p className="text-muted-foreground text-sm">Loading report settings...</p>;

  const dayOpts = Array.from({ length: 28 }, (_, i) => i + 1);
  const hourOpts = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="scheduled-reports-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-indigo-500" />
          <h3 className="font-semibold text-foreground">Scheduled Compliance Reports</h3>
        </div>
        <button
          onClick={toggleEnabled}
          disabled={saving}
          data-testid="toggle-schedule-btn"
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.enabled ? 'bg-indigo-600' : 'bg-muted'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        {settings.enabled
          ? `Active — reports sent on day ${settings.day_of_month} of each month at ${String(settings.hour).padStart(2, '0')}:00 UTC`
          : 'Disabled — enable to auto-send monthly audit reports to stakeholders'}
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <Label className="text-sm">Day of Month</Label>
          <select value={settings.day_of_month || 1} onChange={e => save({ ...settings, day_of_month: Number(e.target.value) })} className={selectSmCls} data-testid="day-of-month-select">
            {dayOpts.map(d => <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-sm">Time (UTC)</Label>
          <select value={settings.hour || 8} onChange={e => save({ ...settings, hour: Number(e.target.value) })} className={selectSmCls} data-testid="hour-select">
            {hourOpts.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
          </select>
        </div>
      </div>

      <div className="mb-4">
        <Label className="text-sm">Recipients</Label>
        <div className="flex gap-2 mt-1">
          <Input placeholder="stakeholder@company.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && addRecipient()} className="flex-1" data-testid="recipient-email-input" />
          <Button variant="outline" size="sm" onClick={addRecipient} disabled={saving} data-testid="add-recipient-btn"><Plus className="w-4 h-4" /></Button>
        </div>
        {settings.recipients?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {settings.recipients.map(email => (
              <span key={email} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-500/10 text-indigo-400 rounded-full text-xs font-medium" data-testid={`recipient-${email}`}>
                <Mail className="w-3 h-3" />{email}
                <button onClick={() => removeRecipient(email)} className="ml-1 opacity-60 hover:opacity-100"><XCircle className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
        {settings.recipients?.length === 0 && (
          <p className="text-xs text-muted-foreground mt-1">No recipients added yet. Add emails to receive scheduled reports.</p>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="text-xs text-muted-foreground">
          {settings.last_sent ? (
            <>
              Last sent: {new Date(settings.last_sent).toLocaleString()}
              {settings.last_status && (
                <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${settings.last_status === 'success' ? 'bg-green-900/20 text-green-400' : settings.last_status === 'partial' ? 'bg-amber-900/20 text-amber-400' : 'bg-red-900/20 text-red-400'}`}>
                  {settings.last_status}
                </span>
              )}
              {settings.last_error && <p className="text-red-400 mt-1 text-[10px]">{settings.last_error}</p>}
            </>
          ) : 'No reports sent yet'}
        </div>
        <Button onClick={sendNow} disabled={sending || !settings.recipients?.length} size="sm" className="gap-1.5" data-testid="send-report-now-btn">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {sending ? 'Sending...' : 'Send Now'}
        </Button>
      </div>
    </div>
  );
}

// ─── DASHBOARD TAB ───
function DashboardTab({ metrics }) {
  const [exporting, setExporting] = useState(false);
  if (!metrics) return <p className="text-muted-foreground py-8 text-center">Loading...</p>;
  const { documents: d, controls: c, risks: r, evidence: e } = metrics;
  const docPct = d.total ? Math.round((d.approved / d.total) * 100) : 0;
  const ctrlPct = c.total ? Math.round((c.implemented / c.total) * 100) : 0;

  const exportAuditPackage = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/admin/compliance/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Export failed'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename=(.+)/);
      a.href = url;
      a.download = match ? match[1] : 'InterpreterIO_ISO27001_Audit.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Audit package downloaded');
    } catch (err) { toast.error(err.message); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={exportAuditPackage} disabled={exporting} data-testid="export-audit-package-btn" className="gap-2">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {exporting ? 'Generating...' : 'Export Audit Package'}
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={<FileText className="w-5 h-5 text-blue-500" />} label="ISMS Documents" value={`${d.approved}/${d.total}`} sub={`${docPct}% approved`} />
        <MetricCard icon={<Shield className="w-5 h-5 text-emerald-500" />} label="Annex A Controls" value={`${c.implemented}/${c.total}`} sub={`${ctrlPct}% implemented`} />
        <MetricCard icon={<AlertTriangle className="w-5 h-5 text-amber-500" />} label="Open Risks" value={r.open} sub={`${r.critical} critical`} />
        <MetricCard icon={<ClipboardList className="w-5 h-5 text-violet-500" />} label="Evidence Records" value={e.total} sub={`${e.this_month} this month`} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ProgressCard title="Document Readiness" items={[
          { label: 'Approved', count: d.approved, color: 'bg-green-500' },
          { label: 'In Review', count: d.in_review, color: 'bg-amber-500' },
          { label: 'Draft', count: d.draft, color: 'bg-muted-foreground/30' },
        ]} total={d.total} />
        <ProgressCard title="Control Implementation" items={[
          { label: 'Implemented', count: c.implemented, color: 'bg-green-500' },
          { label: 'In Progress', count: c.in_progress, color: 'bg-amber-500' },
          { label: 'Not Started', count: c.not_started, color: 'bg-muted-foreground/30' },
        ]} total={c.total} />
      </div>
      <ScheduledReportsPanel />
    </div>
  );
}

function MetricCard({ icon, label, value, sub }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-3 mb-2">{icon}<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span></div>
      <p className="text-3xl font-bold text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground mt-1">{sub}</p>
    </div>
  );
}

function ProgressCard({ title, items, total }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="font-semibold text-foreground mb-3">{title}</h3>
      <div className="flex h-3 rounded-full overflow-hidden bg-muted mb-3">
        {items.map((it, i) => it.count > 0 && <div key={i} className={`${it.color}`} style={{ width: `${(it.count / total) * 100}%` }} />)}
      </div>
      <div className="flex gap-4 text-xs text-muted-foreground">
        {items.map((it, i) => <span key={i} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${it.color}`} />{it.label}: {it.count}</span>)}
      </div>
    </div>
  );
}

// ─── DOCUMENTS TAB ───
function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  useEffect(() => { api('/api/admin/compliance/documents').then(setDocs).catch(() => {}); }, []);

  const save = async () => {
    try {
      await api(`/api/admin/compliance/documents/${selected.id}`, { method: 'PUT', body: JSON.stringify(form) });
      toast.success('Document updated');
      setSelected(null);
      api('/api/admin/compliance/documents').then(setDocs);
    } catch (e) { toast.error(e.message); }
  };

  const categories = [...new Set(docs.map(d => d.category))];
  return (
    <div className="space-y-4">
      {categories.map(cat => (
        <div key={cat}>
          <h3 className="font-semibold text-foreground mb-2">{cat}</h3>
          <div className="bg-card border border-border rounded-xl overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead className="bg-muted border-b border-border"><tr>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Document</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">ISO Reference</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Version</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Owner</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-border">
                {docs.filter(d => d.category === cat).map(doc => (
                  <tr key={doc.id} className="hover:bg-muted/50">
                    <td className="px-4 py-2">
                      <p className="font-medium text-foreground">{doc.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 max-w-md truncate">{doc.description}</p>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground text-xs">{doc.iso_ref}</td>
                    <td className="px-4 py-2"><span className={`text-xs font-medium px-2 py-1 rounded-full ${DOC_STATUS_COLORS[doc.status]}`}>{doc.status}</span></td>
                    <td className="px-4 py-2 text-muted-foreground">{doc.version}</td>
                    <td className="px-4 py-2 text-muted-foreground">{doc.owner || '-'}</td>
                    <td className="px-4 py-2 text-right"><Button variant="ghost" size="sm" onClick={() => { setSelected(doc); setForm({ status: doc.status, content: doc.content, version: doc.version, owner: doc.owner || '', next_review: doc.next_review || '' }); }}><Edit className="w-4 h-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <Dialog open={!!selected} onOpenChange={o => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selected?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">{selected?.description}</p>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-sm">Status</Label>
                <select value={form.status || ''} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={selectCls}>
                  {DOC_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}</option>)}
                </select></div>
              <div><Label className="text-sm">Version</Label><Input value={form.version || ''} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-sm">Owner</Label><Input value={form.owner || ''} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label className="text-sm">Next Review Date</Label><Input type="date" value={form.next_review || ''} onChange={e => setForm(f => ({ ...f, next_review: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-sm">Document Content</Label>
              <textarea value={form.content || ''} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={12} className={`${textareaCls} font-mono resize-y`} placeholder="Enter or paste the full document content here..." /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button><Button onClick={save}>Save Document</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── SoA TAB ───
function SoATab() {
  const [controls, setControls] = useState([]);
  const [filter, setFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => { api('/api/admin/compliance/soa').then(setControls).catch(() => {}); }, []);

  const save = async () => {
    try {
      await api(`/api/admin/compliance/soa/${selected.id}`, { method: 'PUT', body: JSON.stringify(form) });
      toast.success('Control updated');
      setSelected(null);
      api('/api/admin/compliance/soa').then(setControls);
    } catch (e) { toast.error(e.message); }
  };

  const filtered = controls.filter(c =>
    (!filter || c.title.toLowerCase().includes(filter.toLowerCase()) || c.id.toLowerCase().includes(filter.toLowerCase())) &&
    (!catFilter || c.category === catFilter)
  );

  const cats = [...new Set(controls.map(c => c.category))];
  const stats = { implemented: controls.filter(c => c.implementation_status === 'implemented').length, in_progress: controls.filter(c => c.implementation_status === 'in_progress').length, total: controls.length };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" />{stats.implemented} Implemented</span>
        <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-amber-500" />{stats.in_progress} In Progress</span>
        <span className="flex items-center gap-1"><XCircle className="w-4 h-4 text-muted-foreground" />{stats.total - stats.implemented - stats.in_progress} Not Started</span>
      </div>
      <div className="flex gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search controls..." className="pl-9" value={filter} onChange={e => setFilter(e.target.value)} /></div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="h-10 px-3 border border-border rounded-md text-sm bg-background text-foreground">
          <option value="">All Categories</option>{cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted border-b border-border"><tr>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground w-24">ID</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">Control</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground w-28">Category</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground w-24">Applicable</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground w-32">Status</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground w-28">Owner</th>
              <th className="text-right px-4 py-2 font-medium text-muted-foreground w-16">Edit</th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-muted/50">
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{c.id}</td>
                  <td className="px-4 py-2"><span className="font-medium text-foreground">{c.title}</span></td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{c.category}</td>
                  <td className="px-4 py-2">{c.applicable ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}</td>
                  <td className="px-4 py-2"><span className={`text-xs font-medium px-2 py-1 rounded-full ${SOA_STATUS_COLORS[c.implementation_status]}`}>{SOA_STATUS_LABELS[c.implementation_status]}</span></td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{c.owner || '-'}</td>
                  <td className="px-4 py-2 text-right"><Button variant="ghost" size="sm" onClick={() => { setSelected(c); setForm({ applicable: c.applicable, justification: c.justification, implementation_status: c.implementation_status, evidence: c.evidence, owner: c.owner }); }}><Edit className="w-4 h-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={!!selected} onOpenChange={o => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{selected?.id} - {selected?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-3">{selected?.description}</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Applicable</Label>
                <select value={form.applicable ? 'yes' : 'no'} onChange={e => setForm(f => ({ ...f, applicable: e.target.value === 'yes' }))} className={selectCls}>
                  <option value="yes">Yes</option><option value="no">No</option></select></div>
              <div><Label className="text-sm">Implementation Status</Label>
                <select value={form.implementation_status || ''} onChange={e => setForm(f => ({ ...f, implementation_status: e.target.value }))} className={selectCls}>
                  {SOA_STATUSES.map(s => <option key={s} value={s}>{SOA_STATUS_LABELS[s]}</option>)}</select></div>
            </div>
            <div><Label className="text-sm">Owner</Label><Input value={form.owner || ''} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-sm">Justification</Label>
              <textarea value={form.justification || ''} onChange={e => setForm(f => ({ ...f, justification: e.target.value }))} rows={3} className={textareaCls} placeholder="Why this control is applicable/not applicable..." /></div>
            <div><Label className="text-sm">Evidence Reference</Label>
              <textarea value={form.evidence || ''} onChange={e => setForm(f => ({ ...f, evidence: e.target.value }))} rows={2} className={textareaCls} placeholder="Link to evidence or description..." /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button><Button onClick={save}>Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── RISK REGISTER TAB ───
function RiskRegisterTab() {
  const [risks, setRisks] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', category: '', likelihood: 3, impact: 3, treatment: 'mitigate', treatment_plan: '', owner: '', status: 'open', controls: '' });

  const fetchRisks = useCallback(() => { api('/api/admin/compliance/risks').then(setRisks).catch(() => {}); }, []);
  useEffect(() => { fetchRisks(); }, [fetchRisks]);

  const openCreate = () => { setForm({ title: '', description: '', category: '', likelihood: 3, impact: 3, treatment: 'mitigate', treatment_plan: '', owner: '', status: 'open', controls: '' }); setSelected(null); setDialog('create'); };
  const openEdit = (r) => { setForm({ ...r, controls: (r.controls || []).join(', ') }); setSelected(r); setDialog('edit'); };

  const save = async () => {
    try {
      const payload = { ...form, likelihood: Number(form.likelihood), impact: Number(form.impact), controls: form.controls ? form.controls.split(',').map(s => s.trim()) : [] };
      if (dialog === 'create') { await api('/api/admin/compliance/risks', { method: 'POST', body: JSON.stringify(payload) }); toast.success('Risk created'); }
      else { await api(`/api/admin/compliance/risks/${selected.id}`, { method: 'PUT', body: JSON.stringify(payload) }); toast.success('Risk updated'); }
      setDialog(null); fetchRisks();
    } catch (e) { toast.error(e.message); }
  };

  const del = async (r) => { if (!window.confirm(`Delete risk "${r.title}"?`)) return; try { await api(`/api/admin/compliance/risks/${r.id}`, { method: 'DELETE' }); toast.success('Risk deleted'); fetchRisks(); } catch (e) { toast.error(e.message); } };

  const riskColor = (score) => score >= 20 ? 'bg-red-900/20 text-red-400' : score >= 10 ? 'bg-amber-900/20 text-amber-400' : score >= 5 ? 'bg-yellow-900/20 text-yellow-400' : 'bg-green-900/20 text-green-400';

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" />Add Risk</Button></div>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b border-border"><tr>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Risk</th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Category</th>
            <th className="text-center px-4 py-2 font-medium text-muted-foreground">L</th>
            <th className="text-center px-4 py-2 font-medium text-muted-foreground">I</th>
            <th className="text-center px-4 py-2 font-medium text-muted-foreground">Score</th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Treatment</th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Owner</th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Status</th>
            <th className="text-right px-4 py-2 font-medium text-muted-foreground">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {risks.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">No risks recorded. Click "Add Risk" to begin your risk assessment.</td></tr> : risks.map(r => (
              <tr key={r.id} className="hover:bg-muted/50">
                <td className="px-4 py-2"><p className="font-medium text-foreground">{r.title}</p><p className="text-xs text-muted-foreground truncate max-w-xs">{r.description}</p></td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{r.category || '-'}</td>
                <td className="px-4 py-2 text-center text-foreground">{r.likelihood}</td>
                <td className="px-4 py-2 text-center text-foreground">{r.impact}</td>
                <td className="px-4 py-2 text-center"><span className={`text-xs font-bold px-2 py-1 rounded-full ${riskColor(r.risk_score)}`}>{r.risk_score}</span></td>
                <td className="px-4 py-2 text-xs capitalize text-muted-foreground">{r.treatment}</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{r.owner || '-'}</td>
                <td className="px-4 py-2"><span className={`text-xs font-medium px-2 py-1 rounded-full ${r.status === 'open' ? 'bg-red-900/20 text-red-400' : r.status === 'mitigated' ? 'bg-green-900/20 text-green-400' : 'bg-muted text-muted-foreground'}`}>{r.status}</span></td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!dialog} onOpenChange={o => { if (!o) setDialog(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{dialog === 'create' ? 'Add Risk' : 'Edit Risk'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-sm">Description</Label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className={textareaCls} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Category</Label><Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="mt-1" placeholder="e.g. Technical, Operational" /></div>
              <div><Label className="text-sm">Owner</Label><Input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-sm">Likelihood (1-5)</Label><Input type="number" min={1} max={5} value={form.likelihood} onChange={e => setForm(f => ({ ...f, likelihood: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-sm">Impact (1-5)</Label><Input type="number" min={1} max={5} value={form.impact} onChange={e => setForm(f => ({ ...f, impact: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-sm">Score</Label><p className="mt-1 h-10 flex items-center px-3 border border-border rounded-md text-sm font-bold bg-muted text-foreground">{(form.likelihood || 0) * (form.impact || 0)}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Treatment</Label>
                <select value={form.treatment} onChange={e => setForm(f => ({ ...f, treatment: e.target.value }))} className={selectCls}>
                  {RISK_TREATMENTS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}</select></div>
              <div><Label className="text-sm">Status</Label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={selectCls}>
                  <option value="open">Open</option><option value="mitigated">Mitigated</option><option value="accepted">Accepted</option><option value="closed">Closed</option></select></div>
            </div>
            <div><Label className="text-sm">Treatment Plan</Label><textarea value={form.treatment_plan} onChange={e => setForm(f => ({ ...f, treatment_plan: e.target.value }))} rows={3} className={textareaCls} /></div>
            <div><Label className="text-sm">Related Controls (comma-separated)</Label><Input value={form.controls} onChange={e => setForm(f => ({ ...f, controls: e.target.value }))} className="mt-1" placeholder="A.5.7, A.8.8" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button><Button onClick={save}>{dialog === 'create' ? 'Create Risk' : 'Save Changes'}</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── EVIDENCE LOG TAB ───
function EvidenceTab() {
  const [evidence, setEvidence] = useState([]);
  const [filter, setFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ event_type: 'manual', description: '', controls: '' });

  useEffect(() => { api('/api/admin/compliance/evidence').then(setEvidence).catch(() => {}); }, []);

  const addEvidence = async () => {
    try {
      await api('/api/admin/compliance/evidence', { method: 'POST', body: JSON.stringify({ ...form, controls: form.controls ? form.controls.split(',').map(s => s.trim()) : [] }) });
      toast.success('Evidence added');
      setShowAdd(false);
      api('/api/admin/compliance/evidence').then(setEvidence);
    } catch (e) { toast.error(e.message); }
  };

  const filtered = evidence.filter(e => !filter || e.description?.toLowerCase().includes(filter.toLowerCase()) || e.event_type?.includes(filter.toLowerCase()));
  const sourceIcon = (src) => src === 'automatic' ? <Shield className="w-3 h-3 text-blue-500" /> : <FileText className="w-3 h-3 text-muted-foreground" />;

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search evidence..." className="pl-9" value={filter} onChange={e => setFilter(e.target.value)} /></div>
        <Button onClick={() => setShowAdd(true)}><Plus className="w-4 h-4 mr-1" />Add Evidence</Button>
      </div>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b border-border"><tr>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Timestamp</th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Type</th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Description</th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Controls</th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Source</th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Actor</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {filtered.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No evidence records yet. Actions like user management and logins are automatically logged.</td></tr> : filtered.map(e => (
              <tr key={e.id} className="hover:bg-muted/50">
                <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                <td className="px-4 py-2"><span className="text-xs font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground">{e.event_type}</span></td>
                <td className="px-4 py-2 text-foreground max-w-sm truncate">{e.description}</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{(e.controls || []).join(', ') || '-'}</td>
                <td className="px-4 py-2"><span className="flex items-center gap-1 text-xs text-muted-foreground">{sourceIcon(e.source)}{e.source}</span></td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{e.actor || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Manual Evidence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Event Type</Label><Input value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))} className="mt-1" placeholder="e.g. policy_review, training_completed" /></div>
            <div><Label className="text-sm">Description *</Label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className={textareaCls} placeholder="Describe the evidence..." /></div>
            <div><Label className="text-sm">Related Controls (comma-separated)</Label><Input value={form.controls} onChange={e => setForm(f => ({ ...f, controls: e.target.value }))} className="mt-1" placeholder="A.5.1, A.6.3" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button><Button onClick={addEvidence}>Add Evidence</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── MAIN PAGE ───
export default function AdminCompliancePage() {
  const [metrics, setMetrics] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const refreshMetrics = () => api('/api/admin/compliance/dashboard').then(setMetrics).catch(() => {});
  useEffect(() => { refreshMetrics(); }, []);

  const needsSeed = metrics && metrics.documents?.approved === 0 && metrics.controls?.implemented === 0;

  const seedData = async () => {
    setSeeding(true);
    try {
      await api('/api/admin/compliance/seed', { method: 'POST' });
      toast.success('Compliance data seeded — 35 documents, 93 controls, 12 risks');
      refreshMetrics();
      // Force child tabs to re-fetch on next render
      window.location.reload();
    } catch (e) { toast.error(e.message); }
    finally { setSeeding(false); }
  };

  return (
    <AdminPageShell title="ISO 27001 Compliance" subtitle="Information Security Management System" testId="compliance-page">
      {needsSeed && (
        <div className="mb-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 flex items-center justify-between" data-testid="seed-compliance-banner">
          <div>
            <p className="font-medium text-foreground">Compliance data not yet initialized</p>
            <p className="text-sm text-muted-foreground mt-0.5">Populate 35 ISMS documents, 93 Annex A controls, risk register, and evidence baseline.</p>
          </div>
          <Button onClick={seedData} disabled={seeding} className="gap-2 shrink-0 ml-4" data-testid="seed-compliance-btn">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {seeding ? 'Seeding...' : 'Initialize Compliance Data'}
          </Button>
        </div>
      )}
      <Tabs defaultValue="dashboard" className="space-y-4">
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="documents">Documents ({metrics?.documents?.total || 35})</TabsTrigger>
          <TabsTrigger value="soa">Statement of Applicability ({metrics?.controls?.total || 93})</TabsTrigger>
          <TabsTrigger value="risks">Risk Register</TabsTrigger>
          <TabsTrigger value="evidence">Evidence Log</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><DashboardTab metrics={metrics} /></TabsContent>
        <TabsContent value="documents"><DocumentsTab /></TabsContent>
        <TabsContent value="soa"><SoATab /></TabsContent>
        <TabsContent value="risks"><RiskRegisterTab /></TabsContent>
        <TabsContent value="evidence"><EvidenceTab /></TabsContent>
      </Tabs>
    </AdminPageShell>
  );
}
